import { adDescription } from './ad-description';
import "./brand-content.css";
import { singleCopyAssets } from "@shared/angleCopy";
import { useEffect, useState, useRef, useId } from "react";
import { Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { CopyAssets } from '@shared/schema';
import { legacyVisibleCopy } from '@shared/creativeBrief';
import { VisibleCopyEditor } from './VisibleCopyEditor';


export interface AngleDetail {
  id: number;
  brandId: number;
  name: string;
  hookHeadline: string;
  description: string;
  funnelStage: string;
  angleCategory: string;
  adStyle: string;
  emotionalDriver: string;
  awarenessLevel: string | null;
  targetAudience: string | null;
  painPoint: string | null;
  desiredOutcome: string | null;
  proofPoints: string[];
  objectionsAddressed: string[];
  copyAssets: CopyAssets;
  generatedAd?: { src: string; matchesBrief: boolean } | null;
  status: "approved" | "edited" | "archived";
  confidenceScore: number | null;
  sourceRef: Record<string, any> | null;
}

interface AngleCopyDrawerProps {
  angle: AngleDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Dot-path into CopyAssets for each editable string list. */
type CopyPath =
  | ["hookVariations"]
  | ["adImage", "headlines"]
  | ["adImage", "subheadlines"]
  | ["adImage", "badges"]
  | ["meta", "primaryTexts"]
  | ["meta", "shortHeadlines"]
  | ["meta", "descriptions"]
  | ["ctaButtons"];

function getList(assets: CopyAssets, path: CopyPath): string[] {
  return path.length === 1
    ? ((assets as any)[path[0]] as string[])
    : ((assets as any)[path[0]][path[1]] as string[]);
}

function withList(assets: CopyAssets, path: CopyPath, items: string[]): CopyAssets {
  if (path.length === 1) return { ...assets, [path[0]]: items } as CopyAssets;
  return {
    ...assets,
    [path[0]]: { ...(assets as any)[path[0]], [path[1]]: items },
  } as CopyAssets;
}

/** Edit directly in the existing row and persist on blur. */
function CopyItem({ id, label, value, onSave, saving, multiline = false }: { id: string; label: string; value: string; onSave: (next: string) => void; saving: boolean; multiline?: boolean }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (areaRef.current) { areaRef.current.style.height = "auto"; areaRef.current.style.height = `${areaRef.current.scrollHeight}px`; } }, [draft]);
  const props = {
    'data-testid': 'copy-item',
    id,
    'aria-label': label,
    title: 'Click to edit',
    value: draft,
    disabled: saving,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(event.target.value),
    onBlur: () => {
      const next = draft.trim();
      if (!next) { setDraft(value); return; }
      if (next !== value) onSave(next);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && (!multiline || event.metaKey || event.ctrlKey)) event.currentTarget.blur();
      if (event.key === 'Escape') setDraft(value);
    },
    className: 'w-full min-w-0 resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-1 text-sm transition-colors hover:border-slate-200 hover:bg-slate-50 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-slate-200 disabled:opacity-60',
  };
  return multiline ? <textarea {...props} ref={areaRef} rows={1} /> : <input {...props} />;
}

function CopySection({
  title,
  items,
  onEditItem,
  saving,
}: {
  title: string;
  items: string[];
  onEditItem: (index: number, next: string) => void;
  saving: boolean;
}) {
  const sectionId = useId();
  const fieldsRef = useRef<HTMLDivElement>(null);
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        <button
          type="button"
          aria-label={`Edit ${title.toLowerCase()}`}
          aria-controls={`${sectionId}-0`}
          disabled={saving}
          onClick={() => fieldsRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')?.focus()}
          className="rounded px-1 py-0.5 text-[11px] font-normal text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:opacity-50"
        >Edit</button>
      </div>
      <div ref={fieldsRef} className="space-y-1.5">
        {items.map((s, i) => (
          <CopyItem
            key={i}
            id={`${sectionId}-${i}`}
            label={`${title}${items.length > 1 ? ` ${i + 1}` : ''}`}
            value={s}
            multiline={title === "Primary text"}
            saving={saving}
            onSave={(next) => onEditItem(i, next)}
          />
        ))}
      </div>
    </div>
  );
}

export function AngleCopyDrawer({ angle, open, onOpenChange }: AngleCopyDrawerProps) {
  const qc = useQueryClient();
  const { data: detail } = useQuery<AngleDetail>({
    queryKey: ['angle-detail', angle?.id],
    enabled: open && !!angle,
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/ad-angles/${angle!.id}`);
      return response.json();
    },
    staleTime: 0,
  });


  // Local working copy of the assets so edits render instantly; the PATCH
  // persists and the queries refetch in the background. Re-seeded whenever a
  // different angle is opened.
  const [assets, setAssets] = useState<CopyAssets | null>(angle?.copyAssets ? singleCopyAssets(angle.copyAssets, angle.hookHeadline) : null);
  useEffect(() => {
    const current = detail ?? angle;
    setAssets(current?.copyAssets ? singleCopyAssets(current.copyAssets, current.hookHeadline) : null);
  }, [angle?.id, angle?.copyAssets, detail?.copyAssets, detail?.hookHeadline]);

  const saveAssets = useMutation({
    mutationFn: async (next: CopyAssets) => {
      if (!angle) return;
      await apiRequest("PATCH", `/api/ad-angles/${angle.id}`, { copyAssets: next });
    },
    onSuccess: () => {
      // Set-detail, brand angle list, and folder cards all render this copy.
      qc.invalidateQueries({ queryKey: ["angle-detail"] });
      qc.invalidateQueries({ queryKey: ["angle-set"] });
      qc.invalidateQueries({ queryKey: ["ad-angles"] });
      qc.invalidateQueries({ queryKey: ["angle-sets"] });
    },
    onError: (err) => {
      // Re-seed from the server's truth — the optimistic local copy is stale.
      setAssets(angle?.copyAssets ? singleCopyAssets(angle.copyAssets, angle.hookHeadline) : null);
      toast({ description: `Save failed: ${(err as Error).message}`, variant: "destructive" });
    },
  });

  const applyChange = (path: CopyPath, mutate: (items: string[]) => string[], successNote?: string) => {
    if (!assets) return;
    const next = withList(assets, path, mutate(getList(assets, path)));
    setAssets(next);
    saveAssets.mutate(next, {
      onSuccess: () => { if (successNote) toast({ description: successNote }); },
    });
  };

  const sectionHandlers = (path: CopyPath) => ({
    onEditItem: (i: number, value: string) =>
      applyChange(path, (items) => items.map((s, idx) => (idx === i ? value : s)), "Saved"),
    saving: saveAssets.isPending,
  });

  const archive = useMutation({
    mutationFn: async () => {
      if (!angle) return;
      await apiRequest("DELETE", `/api/ad-angles/${angle.id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ad-angles", angle?.brandId] });
      qc.invalidateQueries({ queryKey: ["angle-detail"] });
      qc.invalidateQueries({ queryKey: ["angle-set"] });
      qc.invalidateQueries({ queryKey: ["angle-sets"] });
      onOpenChange(false);
      toast({ description: "Angle deleted" });
    },
  });

  if (!angle || !assets) return null;
  const description = adDescription(assets.adImage.visibleCopy ?? legacyVisibleCopy(assets.adImage), assets.adImage.visualPlan);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="brand-content sm:max-w-2xl overflow-y-auto bg-white" data-testid="angle-copy-drawer">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div className="flex-1 min-w-0">
              <SheetTitle className="brand-detail-title">{angle.name}</SheetTitle>
              <div className="flex gap-1 mt-2 flex-wrap">
                <Badge variant="secondary">{angle.angleCategory}</Badge>
                <Badge variant="outline">{angle.adStyle}</Badge>

              </div>
            </div>
          </div>
        </SheetHeader>

        <Separator className="my-4" />

        <div className="space-y-7">
          {detail?.generatedAd && (
            <figure className="space-y-2">
              <img src={detail.generatedAd.src} alt={`Latest generated ad for ${angle.name}`} className="w-full max-h-72 object-contain rounded-xl" />
              <figcaption className="text-xs text-muted-foreground">
                {detail.generatedAd.matchesBrief ? 'Latest generated ad' : 'Earlier creative — generate again to apply this brief.'}
              </figcaption>
            </figure>
          )}

          <section className="rounded-xl border border-[#DCE3ED] bg-white p-4 space-y-4">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#5F6D82]">On the ad image</h3>
            </div>
            <VisibleCopyEditor value={assets.adImage.visibleCopy ?? legacyVisibleCopy(assets.adImage)} saving={saveAssets.isPending} onSave={visibleCopy=>{const next={...assets,adImage:{...assets.adImage,visibleCopy}};setAssets(next);saveAssets.mutate(next);}} />
          </section>

          <section className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">Ad caption</h3>
            </div>
            <CopySection title="Primary text" items={assets.meta.primaryTexts} {...sectionHandlers(["meta", "primaryTexts"])} />
            <CopySection title="Headline" items={assets.meta.shortHeadlines} {...sectionHandlers(["meta", "shortHeadlines"])} />
            <CopySection title="Description" items={assets.meta.descriptions} {...sectionHandlers(["meta", "descriptions"])} />
            <CopySection title="Call to action" items={assets.ctaButtons} {...sectionHandlers(["ctaButtons"])} />
          </section>

          {description && <section className="border-t pt-4 space-y-3 text-sm">
            <h3 className="font-semibold">The ad</h3>
            <p className="text-muted-foreground leading-relaxed">{description}</p>
          </section>}
        </div>

        <div className="mt-8 flex justify-start border-t pt-4">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
            data-testid="delete-angle-drawer"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {archive.isPending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
